import { useState } from 'react';
import { Server, RefreshCw, Upload, Shield, HardDrive, Save, AlertCircle, Info } from 'lucide-react';

// Badge que indica que el campo es referencial (no tiene efecto funcional aún)
const BadgeReferencial = () => (
  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 border border-amber-200">
    <Info size={10} />
    Referencial
  </span>
);

// Toggle con el mismo estilo que "Aplicar IVA" en Contenedores
const Toggle = ({ value, onChange, activeLabel, inactiveLabel }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        value ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        value ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
    {(activeLabel || inactiveLabel) && (
      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
        value ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {value ? (activeLabel || 'ON') : (inactiveLabel || 'OFF')}
      </span>
    )}
  </div>
);

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function TabSistema({ configuracion, onSave, showToast, setHasChanges }) {
  const [formData, setFormData] = useState({
    auto_recalculo_activo: configuracion.auto_recalculo_activo ?? true,
    recalculo_frecuencia_min: configuracion.recalculo_frecuencia_min || 30,
    recalculo_hora_diario: configuracion.recalculo_hora_diario || '00:05',
    upload_max_size_mb: configuracion.upload_max_size_mb || 10,
    upload_formatos_permitidos: configuracion.upload_formatos_permitidos || 'xlsx,xls',
    seguridad_intentos_max: configuracion.seguridad_intentos_max || 5,
    seguridad_bloqueo_min: configuracion.seguridad_bloqueo_min || 30,
    seguridad_token_horas: configuracion.seguridad_token_horas || 8,
    backup_auto_activo: configuracion.backup_auto_activo ?? true,
    limpieza_auto_dia: configuracion.limpieza_auto_dia ?? 0,
    limpieza_auto_hora: configuracion.limpieza_auto_hora || '03:00',
  });

  const [saving, setSaving] = useState(false);

  const handleToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    setHasChanges(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNumberChange = (field, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setFormData(prev => ({ ...prev, [field]: num }));
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    if (saving) return;

    if (formData.recalculo_frecuencia_min < 5 || formData.recalculo_frecuencia_min > 1440) {
      showToast('La frecuencia de recálculo debe estar entre 5 y 1440 minutos', 'error');
      return;
    }

    if (formData.seguridad_intentos_max < 1 || formData.seguridad_intentos_max > 20) {
      showToast('Los intentos de login deben estar entre 1 y 20', 'error');
      return;
    }

    if (formData.seguridad_bloqueo_min < 1 || formData.seguridad_bloqueo_min > 1440) {
      showToast('El tiempo de bloqueo debe estar entre 1 y 1440 minutos', 'error');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Server size={24} className="text-ink-700" />
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Configuración del Sistema</h2>
          <p className="text-sm text-ink-500">Parámetros técnicos y mantenimiento</p>
        </div>
      </div>

      {/* Aviso general */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Configuración referencial</p>
          <p className="mt-0.5">Los campos marcados con <span className="font-semibold">Referencial</span> registran el valor en la base de datos pero aún no modifican el comportamiento del sistema en esta versión.</p>
        </div>
      </div>

      {/* Recálculo Automático */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <RefreshCw size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-ink-900">Recálculo Automático</h3>
              <p className="text-sm text-ink-500">Frecuencia de actualización de demoras</p>
            </div>
          </div>
          <BadgeReferencial />
        </div>

        <div className="space-y-5">
          {/* auto_recalculo_activo */}
          <div className="flex items-center justify-between py-3 border-b border-ink-100">
            <div>
              <p className="text-sm font-medium text-ink-800">Recálculo automático activo</p>
              <p className="text-xs text-ink-400 mt-0.5">Actualmente siempre activo — cada 30 min y a las 00:05</p>
            </div>
            <Toggle
              value={formData.auto_recalculo_activo}
              onChange={() => handleToggle('auto_recalculo_activo')}
              activeLabel="ACTIVO"
              inactiveLabel="INACTIVO"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Frecuencia (minutos)
              </label>
              <input
                type="number"
                value={formData.recalculo_frecuencia_min}
                onChange={(e) => handleNumberChange('recalculo_frecuencia_min', e.target.value)}
                min="5"
                max="1440"
                step="5"
                className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
              />
              <p className="text-xs text-ink-400 mt-1">Actualmente fijo en 30 minutos</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Hora del recálculo diario
              </label>
              <input
                type="time"
                value={formData.recalculo_hora_diario}
                onChange={(e) => handleChange('recalculo_hora_diario', e.target.value)}
                className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
              />
              <p className="text-xs text-ink-400 mt-1">Actualmente fijo a las 00:05</p>
            </div>
          </div>
        </div>
      </div>

      {/* Archivos */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Upload size={20} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-ink-900">Carga de Archivos</h3>
              <p className="text-sm text-ink-500">Límites y formatos permitidos</p>
            </div>
          </div>
          <BadgeReferencial />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Tamaño máximo (MB)
            </label>
            <input
              type="number"
              value={formData.upload_max_size_mb}
              onChange={(e) => handleNumberChange('upload_max_size_mb', e.target.value)}
              min="1"
              max="100"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">Actualmente fijo en 10 MB</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Formatos permitidos
            </label>
            <input
              type="text"
              value={formData.upload_formatos_permitidos}
              onChange={(e) => handleChange('upload_formatos_permitidos', e.target.value)}
              placeholder="xlsx,xls"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">Actualmente solo xlsx y xls (fijo)</p>
          </div>
        </div>
      </div>

      {/* Seguridad */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield size={20} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-ink-900">Seguridad de Acceso</h3>
              <p className="text-sm text-ink-500">Control de intentos de login y tokens</p>
            </div>
          </div>
          <BadgeReferencial />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Intentos máximos de login
            </label>
            <input
              type="number"
              value={formData.seguridad_intentos_max}
              onChange={(e) => handleNumberChange('seguridad_intentos_max', e.target.value)}
              min="1"
              max="20"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">Antes de bloquear la cuenta</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Duración del bloqueo (min)
            </label>
            <input
              type="number"
              value={formData.seguridad_bloqueo_min}
              onChange={(e) => handleNumberChange('seguridad_bloqueo_min', e.target.value)}
              min="1"
              max="1440"
              step="5"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">
              {formData.seguridad_bloqueo_min >= 60
                ? `${Math.floor(formData.seguridad_bloqueo_min / 60)}h ${formData.seguridad_bloqueo_min % 60 > 0 ? `${formData.seguridad_bloqueo_min % 60}min` : ''}`
                : `${formData.seguridad_bloqueo_min} minutos`}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Vigencia token reset (horas)
            </label>
            <input
              type="number"
              value={formData.seguridad_token_horas}
              onChange={(e) => handleNumberChange('seguridad_token_horas', e.target.value)}
              min="1"
              max="168"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">Horas que dura el link de restablecimiento</p>
          </div>
        </div>
      </div>

      {/* Mantenimiento */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <HardDrive size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-ink-900">Mantenimiento Automático</h3>
              <p className="text-sm text-ink-500">Backup y limpieza programada</p>
            </div>
          </div>
          <BadgeReferencial />
        </div>

        <div className="space-y-5">
          {/* backup_auto_activo */}
          <div className="flex items-center justify-between py-3 border-b border-ink-100">
            <div>
              <p className="text-sm font-medium text-ink-800">Backup automático activo</p>
              <p className="text-xs text-ink-400 mt-0.5">Genera una copia de seguridad de la base de datos</p>
            </div>
            <Toggle
              value={formData.backup_auto_activo}
              onChange={() => handleToggle('backup_auto_activo')}
              activeLabel="ACTIVO"
              inactiveLabel="INACTIVO"
            />
          </div>

          {/* Limpieza */}
          <div className="pt-1">
            <p className="text-sm font-medium text-ink-700 mb-3">Limpieza automática</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-2">Día de la semana</label>
                <select
                  value={formData.limpieza_auto_dia}
                  onChange={(e) => handleChange('limpieza_auto_dia', parseInt(e.target.value, 10))}
                  className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent bg-white"
                >
                  {DIAS_SEMANA.map((dia, idx) => (
                    <option key={idx} value={idx}>{dia}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-600 mb-2">Hora de ejecución</label>
                <input
                  type="time"
                  value={formData.limpieza_auto_hora}
                  onChange={(e) => handleChange('limpieza_auto_hora', e.target.value)}
                  className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-xs text-ink-400 mt-2">
              Actualmente fijo: Domingos a las 03:00 — Elimina notificaciones leídas de más de 30 días
            </p>
          </div>
        </div>
      </div>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>Guardar Configuración</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default TabSistema;
